/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { signalStore, withMethods, withState, patchState } from '@ngrx/signals';
import { debounceTime, Subject } from 'rxjs';

export interface UiStateStoreInitConfig {
  uiStateFields: string[];
  uiStateStoreAs?: { [key: string]: string }[];
  uiStateReactive?: boolean;
  uiStateQueryPath?: string | null;
}

interface UiStateStoreState {
  uiStateFields: string[];
  uiStateStoreAs: { [key: string]: string }[];
  uiStateReactive: boolean;
  uiStateQueryPath: string | null;
  queryParamsChanges: number;
}

const initialState: UiStateStoreState = {
  uiStateFields: [],
  uiStateStoreAs: [],
  uiStateReactive: false,
  uiStateQueryPath: null,
  queryParamsChanges: 0,
};

function flattenObject(value: unknown, prefix = '', result: Record<string, unknown> = {}): Record<string, unknown> {
  if (value === null || value === undefined) {
    return result;
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    result[prefix] = value;
    return result;
  }

  const objectValue = value as Record<string, unknown>;
  Object.keys(objectValue).forEach((key) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    flattenObject(objectValue[key], nextPrefix, result);
  });

  return result;
}

export const UiStateStore = signalStore(
  withState(initialState),

  withMethods((store) => {
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const location = inject(Location);

    const urlSyncSubject = new Subject<void>();

    let pendingQueryParams: Record<string, string> = {};

    const urlSyncSubscription = urlSyncSubject.pipe(debounceTime(300)).subscribe(() => {
      const currentUrl = location.path(true) || router.url;
      const urlTree = router.parseUrl(currentUrl);
      router.navigate([], {
        relativeTo: activatedRoute,
        queryParams: { ...urlTree.queryParams, ...pendingQueryParams },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      pendingQueryParams = {};
    });

    function mapTo(value: unknown): unknown {
      if (!value) {
        return null;
      }

      try {
        return JSON.parse(String(value));
      } catch {
        return null;
      }
    }

    function mapToQueryParamsSchema(value: unknown, schema: { [key: string]: string }): string {
      const flattenValue = flattenObject(value);
      const temporary: { [key: string]: unknown } = {};

      Object.keys(schema).forEach((key) => {
        temporary[key] = flattenValue[schema[key]];
      });

      return JSON.stringify(temporary);
    }

    function getFullField(field: string): string {
      return store.uiStateQueryPath() ? `${store.uiStateQueryPath()}_${field}` : field;
    }

    function generateQueryParams(field: string, value: unknown, index: number): Record<string, string> {
      const storeAs = store.uiStateStoreAs();
      if (storeAs.length > 0 && storeAs[index]) {
        return { [field]: mapToQueryParamsSchema(value, storeAs[index]) };
      }

      return { [field]: JSON.stringify(value) };
    }

    return {
      init(config: UiStateStoreInitConfig): void {
        patchState(store, {
          uiStateFields: config.uiStateFields,
          uiStateStoreAs: config.uiStateStoreAs ?? [],
          uiStateReactive: config.uiStateReactive ?? false,
          uiStateQueryPath: config.uiStateQueryPath ?? null,
          queryParamsChanges: 0,
        });
      },

      getStateFromRouter(): Record<string, unknown> {
        const state: Record<string, unknown> = {};
        const queryParams = activatedRoute.snapshot.queryParams;

        store.uiStateFields().forEach((field) => {
          const fullField = getFullField(field);
          const fieldValue = mapTo(queryParams[fullField]);
          if (fieldValue) {
            state[field] = fieldValue;
          }
        });

        return state;
      },

      shouldProcessQueryParamsChange(): boolean {
        if (store.uiStateReactive()) {
          return true;
        }

        const current = store.queryParamsChanges();
        const shouldProcess = current <= 1;
        patchState(store, { queryParamsChanges: current + 1 });
        return shouldProcess;
      },

      save(field: string, value: unknown): void {
        const index = store.uiStateFields().indexOf(field);
        const fullField = getFullField(field);
        const queryParams = generateQueryParams(fullField, value, index);
        pendingQueryParams = { ...pendingQueryParams, ...queryParams };
        urlSyncSubject.next();
      },

      destroy(): void {
        urlSyncSubscription.unsubscribe();
        urlSyncSubject.complete();
      },
    };
  }),
);
